/**
 * productCategories.service.ts
 *
 * The signed-in account's product-item categories (quote_item_costs_category)
 * — the groups products are filed under on Admin > Products. Not to be
 * confused with the product-TYPE categories at /product-categories-lookup.
 *
 * Every call is single-account: apiClient sends X-Target-Company for the
 * account in session, and the API scopes reads and writes to it.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface ProductItemCategory {
  quoteItemCostsCategoryID: number;
  title: string;
  /** Display order. New categories are appended (max + 1) by the API. */
  qiOrder: number | null;
  active?: boolean | number;
  /** Active products filed under this category. */
  products_count?: number;
}

function unwrapCategory(data: unknown): ProductItemCategory {
  const d = (data as { data?: unknown })?.data ?? data;
  return d as ProductItemCategory;
}

/**
 * Active categories, in the API's qiOrder order. includeDeleted also returns
 * the soft-deleted ones (active = 0).
 */
export async function listProductCategories(options: { includeDeleted?: boolean } = {}): Promise<ProductItemCategory[]> {
  const res = await apiClient.get<ApiResponse<ProductItemCategory[]>>(
    '/product-categories',
    options.includeDeleted ? { params: { include_deleted: 1 } } : undefined,
  );
  return Array.isArray(res.data?.data) ? res.data.data : [];
}

/**
 * Titles are unique per account: a taken title answers 422 DUPLICATE_TITLE
 * with a sentence naming the clash (read it with getErrorMessage).
 */
export async function createProductCategory(title: string): Promise<ProductItemCategory> {
  const res = await apiClient.post<ApiResponse<ProductItemCategory>>('/product-categories', { title });
  return unwrapCategory(res.data);
}

/** Rename. Same DUPLICATE_TITLE rule as create. */
export async function updateProductCategory(id: number, title: string): Promise<ProductItemCategory> {
  const res = await apiClient.put<ApiResponse<ProductItemCategory>>(`/product-categories/${id}`, { title });
  return unwrapCategory(res.data);
}

/**
 * Soft delete (active = 0). The products list hides products whose category
 * is inactive, so this hides every product filed under it too.
 */
export async function deleteProductCategory(id: number): Promise<void> {
  await apiClient.delete(`/product-categories/${id}`);
}

export async function restoreProductCategory(id: number): Promise<void> {
  await apiClient.post(`/product-categories/${id}/restore`);
}

/** Save the full category order (writes qiOrder). */
export async function reorderProductCategories(categoryIds: number[]): Promise<void> {
  await apiClient.post('/product-categories/reorder', { categories: categoryIds });
}

/** One product as the clone preview lists it. */
export interface CategoryProductPreview {
  id: number;
  name: string;
  price: string | number | null;
}

/** The ACTIVE products filed under a category (what a clone copies). */
export async function listCategoryProducts(categoryId: number): Promise<CategoryProductPreview[]> {
  const res = await apiClient.get<ApiResponse<Array<Record<string, unknown>>>>(`/product-categories/${categoryId}/products`);
  const rows = Array.isArray(res.data?.data) ? res.data.data : [];
  return rows.map((p) => ({
    id: Number(p.quoteItemCostID),
    name: String(p.strItem ?? ''),
    price: (p.curRate as string | number | null) ?? null,
  }));
}

/**
 * Copy a category and its active products into a new category in the same
 * account. `facilityIds` (optional) replaces every copy's facilities. A taken
 * title answers 422 DUPLICATE_TITLE.
 */
export async function cloneProductCategory(
  categoryId: number,
  title: string,
  facilityIds: number[] = [],
): Promise<{ category: ProductItemCategory; productsCloned: number }> {
  const res = await apiClient.post<{ data: ProductItemCategory; meta?: { products_cloned?: number } }>(
    `/product-categories/${categoryId}/clone`,
    { title, ...(facilityIds.length > 0 ? { facility_ids: facilityIds } : {}) },
  );
  return { category: unwrapCategory(res.data), productsCloned: Number(res.data?.meta?.products_cloned ?? 0) };
}
